import { Badge, Button } from '@aecom-ai-engineering/react-components';
import { Dialog } from '@ark-ui/react/dialog';
import { Portal } from '@ark-ui/react/portal';
import { X } from 'lucide-react';
import { type FC, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { AGENT_MARK_FALLBACK, AGENT_MARKS } from '@/components/agent-marks';

import { agentDetailsContent } from './agent-details-content';
import { AgentDetailsFaqs } from './agent-details-faqs';
import { AgentDetailsGallery } from './agent-details-gallery';
import { type DirectoryAgent } from './filter-agents';
import './agent-details-dialog.css';

const ICON_STROKE_WIDTH = 1.75;

/*
 * The frame's 800px column inside a 1024px card: 112 each side, down to the
 * 24 of the body's own padding on a narrower screen. Header and body share it,
 * so the logo lines up with the text under it.
 */
const COLUMN_PADDING = 'px-[max(var(--ds-space-6),calc((100%-800px)/2))]';

/** 14/20 medium. */
const SECTION_HEADING =
  'text-[length:var(--ds-typography-paragraph-small-font-size)] font-[var(--ds-typography-paragraph-paragraph-medium-weight)] leading-[var(--ds-typography-paragraph-small-line-height)] text-[var(--ds-color-text-primary)]';

/*
 * 14/20 regular in raw-stone-600: the frame's text-secondary is #57534E, and
 * the exported --ds-color-text-secondary is #78716C.
 */
const SECTION_TEXT =
  'text-[length:var(--ds-typography-paragraph-small-font-size)] leading-[var(--ds-typography-paragraph-small-line-height)] text-[var(--ds-color-raw-stone-600)]';

/*
 * Badge is 20px tall with a 6px radius. The frame's pill is 28px, 8px radius,
 * 6px gap. Utilities outrank the design system's components layer.
 */
const PILL = 'min-h-7 gap-1.5 rounded-[var(--ds-radius-8)] py-1.5';

/** Paragraphs 14px apart, the copy's own paragraph spacing. */
const Paragraphs: FC<{ paragraphs: string[] }> = ({ paragraphs }) => (
  <div className="flex flex-col gap-3.5">
    {paragraphs.map((paragraph) => (
      <p className={SECTION_TEXT} key={paragraph}>
        {paragraph}
      </p>
    ))}
  </div>
);

const Section: FC<{ heading: string; children: ReactNode }> = ({ heading, children }) => (
  <section className="flex flex-col gap-2">
    <h3 className={SECTION_HEADING}>{heading}</h3>
    {children}
  </section>
);

interface AgentDetailsDialogProps {
  /** The agent to show. Null keeps the dialog closed. */
  agent: DirectoryAgent | null;
  onClose: () => void;
  /** Where focus goes on close: the card that opened the dialog. */
  finalFocusEl?: () => HTMLElement | null;
}

/**
 * One agent's details, over the agents directory. Figma 11263:75311, and
 * 11268:77836 for the same dialog at a 900px viewport: the header stays and the
 * body scrolls under it.
 *
 * Ark's Dialog rather than the design system's, which is fixed at 640px.
 */
export const AgentDetailsDialog: FC<AgentDetailsDialogProps> = ({
  agent,
  onClose,
  finalFocusEl,
}) => {
  const { t } = useTranslation();
  const content =
    agent === null
      ? null
      : (agentDetailsContent(agent.serviceName, t) ?? {
          about: [agent.description],
          howItWorks: [],
          currentScope: [],
          faqs: [],
          images: [],
          adminOnly: false,
        });

  return (
    <Dialog.Root
      closeOnEscape
      closeOnInteractOutside
      finalFocusEl={finalFocusEl}
      lazyMount
      onOpenChange={({ open }) => {
        if (!open) onClose();
      }}
      open={agent !== null}
      unmountOnExit
    >
      <Portal>
        <Dialog.Backdrop className="fixed inset-0 z-80 bg-[color-mix(in_srgb,var(--ds-color-raw-black)_40%,transparent)]" />
        <Dialog.Positioner className="fixed inset-0 z-80 flex items-center justify-center p-[var(--ds-space-4)]">
          {/* ds-body-font because the content is portaled to body, outside the
              AppShell root that sets the family. */}
          <Dialog.Content className="ds-body-font flex max-h-[calc(100dvh-2*var(--ds-space-12))] w-full max-w-[1024px] flex-col overflow-hidden rounded-[var(--ds-radius-16)] bg-[var(--ds-color-bg-primary)]">
            {agent !== null && content !== null && (
              <>
                <header
                  className={`relative flex shrink-0 items-center gap-4 border-b border-solid border-[var(--ds-color-border-primary)] py-8 ${COLUMN_PADDING}`}
                >
                  <span
                    aria-hidden="true"
                    className="flex shrink-0"
                    dangerouslySetInnerHTML={{
                      __html: AGENT_MARKS[agent.serviceName] ?? AGENT_MARK_FALLBACK,
                    }}
                    data-ds-agent-logo-inline
                  />
                  <div className="flex min-w-0 flex-1 flex-col gap-0.5 pr-10">
                    <Dialog.Title className="wrap-break-word hyphens-auto text-[length:var(--ds-typography-heading-4-font-size)] font-[var(--ds-typography-paragraph-paragraph-medium-weight)] leading-[var(--ds-typography-heading-4-line-height)] tracking-[var(--ds-typography-heading-4-letter-spacing)] text-[var(--ds-color-text-primary)]">
                      {agent.title}
                    </Dialog.Title>
                    <p className="text-[13px] leading-[18px] text-[var(--ds-color-raw-stone-600)]">
                      {/* A middle dot rather than a comma, which ja and zh write differently. */}
                      {agent.categories
                        .map((category) => t(`agents-directory.categories.${category}`))
                        .join(' · ')}
                    </p>
                  </div>
                  <Dialog.CloseTrigger asChild>
                    {/* 34px keeps the icon where the frame puts it: the 40px
                        button is 6px larger on each side than the 28px box. */}
                    <Button
                      aria-label={t('agents-directory.details.close')}
                      className="absolute top-[34px] right-[34px] size-10 min-h-0 rounded-full p-0 text-[var(--ds-color-bg-icon-primary)]"
                      variant="ghost"
                    >
                      <X aria-hidden="true" className="size-6" strokeWidth={ICON_STROKE_WIDTH} />
                    </Button>
                  </Dialog.CloseTrigger>
                </header>

                <div
                  className={`flex min-h-0 flex-col gap-6 overflow-y-auto overscroll-contain pt-4 pb-10 ${COLUMN_PADDING}`}
                >
                  <Section heading={t('agents-directory.details.about')}>
                    <div className="flex flex-col gap-3">
                      <Paragraphs paragraphs={content.about} />
                      {(agent.tags.length > 0 || content.adminOnly) && (
                        <div className="flex flex-wrap gap-1">
                          {agent.tags.map((tag) => (
                            <Badge
                              className={PILL}
                              key={tag.key}
                              label={tag.label}
                              variant="outline"
                            />
                          ))}
                          {content.adminOnly && (
                            <Badge
                              className={PILL}
                              icon="lock"
                              label={t('agents-directory.details.admin-only')}
                              variant="outline"
                            />
                          )}
                        </div>
                      )}
                    </div>
                  </Section>

                  <AgentDetailsGallery images={content.images} />

                  {content.howItWorks.length > 0 && (
                    <Section heading={t('agents-directory.details.how-it-works')}>
                      <Paragraphs paragraphs={content.howItWorks} />
                    </Section>
                  )}

                  {content.currentScope.length > 0 && (
                    <Section heading={t('agents-directory.details.current-scope')}>
                      <Paragraphs paragraphs={content.currentScope} />
                    </Section>
                  )}

                  {content.faqs.length > 0 && (
                    <Section heading={t('agents-directory.details.faqs')}>
                      <AgentDetailsFaqs faqs={content.faqs} />
                    </Section>
                  )}
                </div>
              </>
            )}
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};
